import React, { useState } from "react";
import { FiSend, FiCheckCircle } from "react-icons/fi";
import { useTranslation } from "react-i18next";

import contact from "../assets/images/contact.svg";

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    subject: t("contact.subjectDefault"),
    message: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = t("contact.nameRequired");
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t("contact.nameMinLength");
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = t("contact.emailRequired");
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = t("contact.emailInvalid");
    }

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = t("contact.subjectRequired");
    } else if (formData.subject.trim().length < 5) {
      newErrors.subject = t("contact.subjectMinLength");
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = t("contact.messageRequired");
    } else if (formData.message.trim().length < 10) {
      newErrors.message = t("contact.messageMinLength");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate API call
    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        subject: t("contact.subjectDefault"),
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setErrors({});
  };

  return (
    <section className="">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <h6 className="text-primaryColor uppercase text-sm font-bold tracking-wider mb-3">
            {t("contact.title")}
          </h6>
          <h2 className="mb-6 md:text-3xl text-2xl md:leading-normal leading-normal font-bold">
            {t("contact.subtitle")}
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            {t("contact.description")}
          </p>
        </div>

        {/* Main Content - Illustration and Form */}
        <div className="grid lg:grid-cols-12 md:grid-cols-1 gap-8 md:gap-12 items-start">
          {/* Illustration - Hidden on mobile, shown on tablet and desktop */}
          <div className="lg:col-span-5 hidden md:block">
            <div className="flex justify-center lg:justify-start">
              <img
                src={contact}
                alt="Contact illustration"
                className="w-full max-w-md lg:max-w-2xl mx-auto lg:mx-0"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7 md:col-span-1 ">
            <div className="flex justify-center lg:justify-end ">
              <div className="bg-primaryFaint rounded-xl p-6 md:p-8 lg:p-10 w-full max-w-lg md:max-w-2xl ">
                {isSubmitted ? (
                  <div className="text-center py-6 md:py-8">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FiCheckCircle className="h-6 w-6 md:h-8 md:w-8 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {t("contact.messageSent")}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base mb-6">
                      {t("contact.thankYou")}
                    </p>
                    <button
                      onClick={resetForm}
                      className="px-4 md:px-6 py-2 md:py-3 bg-primaryColor text-white rounded-lg hover:bg-primaryColor/90 transition-colors duration-200 text-sm md:text-base font-medium"
                    >
                      {t("contact.sendAnother")}
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="space-y-4 md:space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                          {t("contact.fullName")}
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor transition-colors duration-200 text-sm md:text-base ${
                            errors.name
                              ? "border-red-500 bg-red-50 dark:bg-red-900/10"
                              : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                          }`}
                          placeholder={t("contact.namePlaceholder")}
                        />
                        {errors.name && (
                          <p className="mt-1 text-xs md:text-sm text-red-600 dark:text-red-400">
                            {errors.name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                          {t("contact.emailAddress")}
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor transition-colors duration-200 text-sm md:text-base ${
                            errors.email
                              ? "border-red-500 bg-red-50 dark:bg-red-900/10"
                              : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                          }`}
                          placeholder={t("contact.emailPlaceholder")}
                        />
                        {errors.email && (
                          <p className="mt-1 text-xs md:text-sm text-red-600 dark:text-red-400">
                            {errors.email}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="subject"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        {t("contact.subject")}
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className={`w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor transition-colors duration-200 text-sm md:text-base ${
                          errors.subject
                            ? "border-red-500 bg-red-50 dark:bg-red-900/10"
                            : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                        }`}
                        placeholder={t("contact.subjectPlaceholder")}
                      />
                      {errors.subject && (
                        <p className="mt-1 text-xs md:text-sm text-red-600 dark:text-red-400">
                          {errors.subject}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        {t("contact.message")}
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        value={formData.message}
                        onChange={handleInputChange}
                        className={`w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor transition-colors duration-200 resize-none text-sm md:text-base ${
                          errors.message
                            ? "border-red-500 bg-red-50 dark:bg-red-900/10"
                            : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                        }`}
                        placeholder={t("contact.messagePlaceholder")}
                      />
                      {errors.message && (
                        <p className="mt-1 text-xs md:text-sm text-red-600 dark:text-red-400">
                          {errors.message}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full flex items-center justify-center px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium transition-all duration-200 text-sm md:text-base ${
                        isSubmitting
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-primaryColor hover:bg-primaryColor/90 text-white"
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 md:h-5 md:w-5 border-b-2 border-white mr-2"></div>
                          {t("contact.sending")}
                        </>
                      ) : (
                        <>
                          <FiSend className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                          {t("contact.sendMessage")}
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
